/**
 * Sync service for Idle Depths.
 *
 * Handles syncing local state to server for authenticated users.
 * Uses debouncing to batch frequent changes.
 */

import * as Sentry from '@sentry/react';

import type { RootStore } from '@/stores/RootStore';

/**
 * Payload for syncing game state to server.
 */
export interface SyncPayload {
  /** Timestamp of the sync */
  timestamp: number;

  /** Type of data being synced */
  type: 'preferences' | 'character' | 'inventory' | 'full';

  /** Data to sync (JSON-serializable) */
  data: unknown;
}

/**
 * Sync service for authenticated users.
 *
 * Manages the sync lifecycle:
 * - Queue changes
 * - Debounce for batching
 * - Send to server
 * - Handle errors gracefully
 */
export class SyncService {
  /** Pending sync queue */
  private pendingSync: Map<string, SyncPayload> = new Map();

  /** Debounce timer */
  private syncTimer: number | null = null;

  /** Sync debounce delay (ms) */
  private readonly SYNC_DEBOUNCE = 2000;

  /** Whether sync is in progress */
  private syncing = false;

  /** Reference to root store */
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * Queue data for sync to server.
   *
   * @param type - Type of data
   * @param data - Data to sync
   */
  queueSync(type: SyncPayload['type'], data: unknown): void {
    // Only sync if authenticated
    if (!this.rootStore.authStore.shouldSyncToServer) {
      return;
    }

    const payload: SyncPayload = {
      timestamp: Date.now(),
      type,
      data,
    };

    // Store in pending queue (overwrites previous of same type)
    this.pendingSync.set(type, payload);

    // Debounce the actual sync
    this.debouncedSync();
  }

  /**
   * Debounced sync trigger.
   */
  private debouncedSync(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      void this.performSync();
    }, this.SYNC_DEBOUNCE);
  }

  /**
   * Perform the actual sync to server.
   */
  private async performSync(): Promise<void> {
    // Skip if already syncing or nothing to sync
    if (this.syncing || this.pendingSync.size === 0) {
      return;
    }

    // Skip if not authenticated
    if (!this.rootStore.authStore.shouldSyncToServer) {
      this.pendingSync.clear();
      return;
    }

    this.syncing = true;

    // Get all pending items and clear queue
    const items = Array.from(this.pendingSync.entries());
    this.pendingSync.clear();

    try {
      // Process each sync item
      for (const [type, payload] of items) {
        await this.syncItem(type as SyncPayload['type'], payload);
      }

      Sentry.addBreadcrumb({
        category: 'sync',
        message: `Synced ${String(items.length)} items to server`,
        level: 'info',
      });
    } catch (error) {
      // Re-queue failed items for retry
      for (const [type, payload] of items) {
        this.pendingSync.set(type, payload);
      }

      Sentry.captureException(error, {
        tags: { context: 'sync_failed' },
        extra: { itemCount: items.length },
      });
    } finally {
      this.syncing = false;

      // If new items were queued during sync, trigger another sync
      if (this.pendingSync.size > 0) {
        this.debouncedSync();
      }
    }
  }

  /**
   * Sync a single item to server.
   */
  private async syncItem(_type: SyncPayload['type'], _payload: SyncPayload): Promise<void> {
    // TODO: Implement actual sync calls when character system is built
    // For now, preferences are synced directly by PreferencesStore
    //
    // switch (type) {
    //   case 'character':
    //     await trpc.character.sync.mutate(payload.data);
    //     break;
    //   case 'inventory':
    //     await trpc.inventory.sync.mutate(payload.data);
    //     break;
    //   // etc.
    // }
  }

  /**
   * Force immediate sync (bypasses debounce).
   *
   * Call before critical actions like logout.
   */
  async flushSync(): Promise<void> {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    await this.performSync();
  }

  /**
   * Clear pending syncs without sending.
   */
  clearPending(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.pendingSync.clear();
  }

  /**
   * Get count of pending sync items.
   */
  get pendingCount(): number {
    return this.pendingSync.size;
  }

  /**
   * Whether sync is currently in progress.
   */
  get isSyncing(): boolean {
    return this.syncing;
  }
}
